import { getValidAccessToken } from '../../../lib/supabase';
import type { AdminListResponse } from '../../../domain/superadmin/types';
import type { AdminPartnerRow } from '../../../domain/partners/adminTypes';
import { SuperadminApiError } from '../superadmin/errors';

type ListParams = {
  page?: number;
  pageSize?: number;
  q?: string;
  status?: string;
};

type ErrorPayload = {
  error?: string;
  items?: AdminPartnerRow[];
  page?: number;
  pageSize?: number;
  total?: number;
  promoted?: number;
};

async function readJsonPayload(response: Response): Promise<ErrorPayload> {
  try {
    return (await response.json()) as ErrorPayload;
  } catch {
    return {};
  }
}

async function partnerAdminFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const accessToken = await getValidAccessToken();
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${path}`;

  const response = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
  });

  const payload = await readJsonPayload(response);
  if (response.status === 403) {
    throw new SuperadminApiError('Forbidden', 403);
  }
  if (!response.ok) {
    throw new Error(payload.error || 'Erro na requisição');
  }
  return payload as T;
}

export async function fetchAdminPartners(params: ListParams): Promise<AdminListResponse<AdminPartnerRow>> {
  const url = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-list-partners`);
  if (params.page) url.searchParams.set('page', String(params.page));
  if (params.pageSize) url.searchParams.set('pageSize', String(params.pageSize));
  if (params.q) url.searchParams.set('q', params.q);
  if (params.status) url.searchParams.set('status', params.status);

  const accessToken = await getValidAccessToken();
  const response = await fetch(url.toString(), {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
  });

  const payload = await readJsonPayload(response);
  if (response.status === 403) {
    throw new SuperadminApiError('Forbidden', 403);
  }
  if (!response.ok) {
    throw new Error(payload.error || 'Erro ao listar parceiros');
  }

  return {
    items: payload.items ?? [],
    page: payload.page ?? 1,
    pageSize: payload.pageSize ?? 25,
    total: payload.total ?? 0,
  };
}

export async function updateAdminPartner(
  partnerId: string,
  patch: { status?: string; commissionRate?: number },
): Promise<void> {
  await partnerAdminFetch('admin-update-partner', {
    method: 'PATCH',
    body: JSON.stringify({ partnerId, ...patch }),
  });
}

export async function promotePartnerCommissions(): Promise<{ promoted: number }> {
  const accessToken = await getValidAccessToken();
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/promote-partner-commissions`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
    },
  );
  const payload = await readJsonPayload(response);
  if (!response.ok) {
    throw new Error(payload.error || 'Erro ao promover comissões');
  }
  return { promoted: payload.promoted ?? 0 };
}
